/* =====================================================================
 * DocxRenderer — DOCX 파일 렌더링 및 편집 컴포넌트
 * 자체 OOXML 파서 + React 편집기 (contenteditable)
 * ===================================================================== */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { DocxPage } from './docx/DocxPage';
import type { DocxPageHandle } from './docx/DocxPage';
import { DocxToolbar } from './docx/DocxToolbar';
import { useDocxEditor } from './docx/useDocxEditor';
import { patchDocx } from '@auw/workspace-utils';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface DocxRendererProps {
  file: File;
  onLoadComplete?: () => void;
  onLoadStart?: () => void;
  onError?: (msg: string) => void;
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const EditorContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  /* 스크롤바 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #c0c0c0;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a0a0a0;
  }
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const DocxRenderer: React.FC<DocxRendererProps> = ({
  file,
  onLoadComplete,
  onLoadStart,
  onError,
}) => {
  const [fileBuffer, setFileBuffer] = useState<ArrayBuffer | null>(null);
  const pageRef = useRef<DocxPageHandle>(null);

  const [editorState, editorActions] = useDocxEditor(
    fileBuffer,
    onLoadComplete,
    onError,
  );

  /* ── 파일 로드 (ArrayBuffer) ── */
  useEffect(() => {
    let cancelled = false;
    onLoadStart?.();

    const reader = new FileReader();
    reader.onload = (e) => {
      if (cancelled) return;
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        if (!arrayBuffer) throw new Error('파일을 읽을 수 없습니다.');
        setFileBuffer(arrayBuffer);
      } catch (err) {
        console.error('[DocxRenderer] Read error:', err);
        onError?.('DOCX 파일을 읽을 수 없습니다.');
      }
    };
    reader.onerror = () => {
      if (cancelled) return;
      onError?.('파일 읽기에 실패했습니다.');
    };
    reader.readAsArrayBuffer(file);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  /* ── 커서 이동 시 서식 상태 갱신 ── */
  useEffect(() => {
    const handler = () => {
      editorActions.updateFormatFromCursor();
    };
    document.addEventListener('selectionchange', handler);
    return () => {
      document.removeEventListener('selectionchange', handler);
    };
  }, [editorActions]);

  /* ── 다운로드: DOM → 모델 동기화 후 export ── */
  const handleDownload = useCallback(async () => {
    try {
      // DOM에서 텍스트 동기화
      const domTexts = pageRef.current?.syncFromDom() ?? new Map<string, string>();
      const syncedDoc = editorActions.syncAndGetDocument(domTexts);

      let blob: Blob;

      if (syncedDoc) {
        const dirtyIds = editorActions.getDirtyBlockIds();
        if (dirtyIds.size > 0) {
          blob = await patchDocx(syncedDoc, dirtyIds);
        } else if (fileBuffer) {
          blob = new Blob([fileBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          });
        } else {
          return;
        }
      } else if (fileBuffer) {
        blob = new Blob([fileBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
      } else {
        console.warn('[DocxRenderer] No buffer to download');
        return;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[DocxRenderer] Download error:', err);
    }
  }, [editorActions, fileBuffer, file.name]);

  return (
    <Container>
      {/* 툴바 — 기능 유지, UI만 숨김 */}
      {editorState.document && (
        <div style={{ display: 'none' }}>
          <DocxToolbar
            formatState={editorState.formatState}
            canUndo={editorState.canUndo}
            canRedo={editorState.canRedo}
            onFormat={editorActions.applyFormat}
            onUndo={editorActions.undo}
            onRedo={editorActions.redo}
            onDownload={handleDownload}
          />
        </div>
      )}

      {/* 편집기 */}
      <EditorContent>
        {editorState.loading && (
          <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>
            문서를 파싱하는 중...
          </div>
        )}
        {editorState.error && (
          <div style={{ padding: 20, textAlign: 'center', color: '#e53e3e' }}>
            {editorState.error}
          </div>
        )}
        {editorState.document && (
          <DocxPage
            ref={pageRef}
            document={editorState.document}
            editable
            onInsertNumberedParagraph={editorActions.insertNumberedParagraphAfter}
            onRemoveNumbering={editorActions.removeNumbering}
          />
        )}
      </EditorContent>
    </Container>
  );
};
